import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { searchSnomed } from "./participantService";
import { snomedCodeOf, snomedTermOf } from "./clinicalHistoryUtils";

const EXTERNAL_TYPES = new Set([
  "all",
  "medicalhistory",
  "surgicalhistory",
  "familyhistory",
  "allergy",
  "medication",
]);

/** Only one portaled SNOMED menu should be open at a time. */
let activeSnomedMenuId = null;
const snomedMenuListeners = new Set();

function setActiveSnomedMenu(id) {
  activeSnomedMenuId = id;
  snomedMenuListeners.forEach((fn) => fn());
}

function termTypeOf(row) {
  return String(
    row?.TermType ??
      row?.termType ??
      row?.ClinicalSection ??
      row?.clinicalSection ??
      "",
  ).trim();
}

function mapRows(rows, limit = 30) {
  const mapped = (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      code: snomedCodeOf(row),
      term: snomedTermOf(row),
      termType: termTypeOf(row),
    }))
    .filter((item) => item.term);
  const seen = new Set();
  const unique = [];
  for (const item of mapped) {
    const key = `${item.code}|${item.term}|${item.termType}`.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
    if (unique.length >= limit) break;
  }
  return unique;
}

/**
 * Same typeahead for every clinical term field.
 * Menu is portaled (fixed) so the modal does not jump while searching.
 */
function SnomedSearchField({
  userData,
  termType,
  label = "Search term",
  placeholder = "Type to search…",
  selected,
  onSelect,
  disabled = false,
  minChars,
  /** When true, clear the input after a successful pick (chip/list UX). */
  clearOnSelect = false,
}) {
  const instanceId = useId();
  const typeKey = String(termType || "").toLowerCase();
  const isExternal = EXTERNAL_TYPES.has(typeKey);
  const effectiveMinChars =
    minChars != null ? minChars : isExternal ? 3 : 0;

  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const [activeTick, setActiveTick] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);
  const focusedRef = useRef(false);

  useEffect(() => {
    const sync = () => setActiveTick((n) => n + 1);
    snomedMenuListeners.add(sync);
    return () => snomedMenuListeners.delete(sync);
  }, []);

  useEffect(() => {
    if (selected?.term) {
      setQuery(selected.term);
      return;
    }
    if (selected === null || selected === undefined) {
      // Keep typed text while searching; parent may pass null for chip UX.
      return;
    }
    setQuery("");
  }, [selected?.term, selected?.code, selected]);

  const updatePos = useCallback(() => {
    const el = inputRef.current || wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 4;
    const preferred = 220;
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const spaceAbove = r.top - 8;
    const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
    const usable = openUpward ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(120, Math.min(preferred, usable));
    setMenuPos({
      position: "fixed",
      left: r.left,
      width: Math.max(r.width, 220),
      zIndex: 2400,
      maxHeight,
      ...(openUpward
        ? { bottom: window.innerHeight - r.top + gap }
        : { top: r.bottom + gap }),
    });
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
    if (activeSnomedMenuId === instanceId) {
      setActiveSnomedMenu(null);
    }
  }, [instanceId]);

  const openMenu = useCallback(() => {
    setActiveSnomedMenu(instanceId);
    setOpen(true);
    requestAnimationFrame(updatePos);
  }, [instanceId, updatePos]);

  useEffect(() => {
    const onDoc = (event) => {
      const inField = wrapRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inField && !inMenu) {
        focusedRef.current = false;
        setFocused(false);
        closeMenu();
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [closeMenu]);

  useEffect(() => {
    if (!open || activeSnomedMenuId !== instanceId) return undefined;
    updatePos();
    const reposition = () => updatePos();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, options.length, updatePos, instanceId, activeTick]);

  useEffect(() => {
    if (!termType || disabled) return undefined;

    const text = String(query || "").trim();

    // Only the focused field should search / show suggestions.
    if (!focusedRef.current) {
      return undefined;
    }

    if (selected?.term && text === selected.term) {
      setOptions([]);
      closeMenu();
      return undefined;
    }

    // Feasibility TermType "All" has no empty/recent list — require min chars.
    if (typeKey === "all" && text.length < effectiveMinChars) {
      setOptions([]);
      closeMenu();
      return undefined;
    }

    // Other external types: typed text under min chars → wait; empty → recent list.
    if (
      isExternal &&
      typeKey !== "all" &&
      text.length > 0 &&
      text.length < effectiveMinChars
    ) {
      setOptions([]);
      return undefined;
    }

    if (!isExternal && !userData?.OrganizationId) return undefined;

    clearTimeout(timerRef.current);
    const requestId = ++requestIdRef.current;
    timerRef.current = setTimeout(async () => {
      if (!focusedRef.current) return;
      setLoading(true);
      try {
        const rows = await searchSnomed(userData, {
          searchText: text,
          termType,
        });
        if (requestId !== requestIdRef.current || !focusedRef.current) return;
        setOptions(mapRows(rows, 30));
        openMenu();
      } catch {
        if (requestId === requestIdRef.current) setOptions([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 280);
    return () => clearTimeout(timerRef.current);
  }, [
    closeMenu,
    disabled,
    effectiveMinChars,
    focused,
    isExternal,
    openMenu,
    query,
    selected?.term,
    termType,
    typeKey,
    userData,
  ]);

  const pickItem = (item) => {
    onSelect?.(item);
    if (clearOnSelect) {
      setQuery("");
      setOptions([]);
    } else {
      setQuery(item.term);
    }
    focusedRef.current = false;
    setFocused(false);
    closeMenu();
    inputRef.current?.blur();
  };

  const text = String(query || "").trim();
  const needsMoreChars =
    isExternal && text.length > 0 && text.length < effectiveMinChars;
  const isActiveMenu = activeSnomedMenuId === instanceId;
  const showMenu =
    open &&
    isActiveMenu &&
    focused &&
    !needsMoreChars &&
    !(selected?.term && text === selected.term) &&
    menuPos &&
    (loading || options.length > 0);

  return (
    <div className="pm-snomed" ref={wrapRef}>
      {label ? <label className="pm-clinical-modal__label">{label}</label> : null}
      <div className="pm-snomed__control">
        <input
          ref={inputRef}
          type="text"
          className="form-control form-control-sm"
          value={query}
          disabled={disabled}
          placeholder={
            isExternal
              ? placeholder || "Type at least 3 characters…"
              : placeholder
          }
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            if (selected) onSelect?.(null);
            focusedRef.current = true;
            setFocused(true);
            setActiveSnomedMenu(instanceId);
            setOpen(true);
            updatePos();
          }}
          onFocus={() => {
            focusedRef.current = true;
            setFocused(true);
            setActiveSnomedMenu(instanceId);
            updatePos();
          }}
          onBlur={() => {
            // Delay so option mousedown can fire first.
            window.setTimeout(() => {
              if (menuRef.current?.contains(document.activeElement)) return;
              focusedRef.current = false;
              setFocused(false);
              closeMenu();
            }, 120);
          }}
        />
        {loading && focused ? (
          <span className="pm-snomed__spinner" aria-hidden="true" />
        ) : null}
      </div>
      {showMenu
        ? createPortal(
            <div
              ref={menuRef}
              className="pm-snomed__menu custom-dropdown"
              style={menuPos}
            >
              <div
                className="dropdown-menu show"
                style={{ maxHeight: menuPos.maxHeight }}
              >
                <ul
                  className="dropdown-list pm-thin-scroll"
                  style={{
                    maxHeight: Math.max(96, (menuPos.maxHeight || 220) - 12),
                  }}
                  role="listbox"
                >
                  {!options.length && loading ? (
                    <li className="dropdown-item pm-snomed__empty">
                      Searching…
                    </li>
                  ) : (
                    options.map((item) => (
                      <li
                        key={`${item.code}-${item.term}-${item.termType || ""}`}
                        className={[
                          "dropdown-item",
                          selected?.term === item.term ? "selected" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        role="option"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          pickItem(item);
                        }}
                      >
                        <span className="text-truncate">
                          {item.term}
                          {item.termType ? ` (${item.termType})` : ""}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export default SnomedSearchField;
